import { useEffect, useMemo } from "react";

import { MENTION_TYPE, SOURCE } from "@/constants";
import { MentionCard as MentionCardType } from "@/types/MentionCard";
import { cx, events } from "@/utils";

import { useEpisodes } from "../Episodes/EpisodesContext";
import { useMentionCard } from "./context";
import AudioMentionCardBody from "./partials/Body/Audio";
import NewsMentionCardBody from "./partials/Body/News";
import TextMentionCardBody from "./partials/Body/Text";
import MentionCardBase from "./partials/EpisodeBase";
import MentionCardMobile from "./partials/EpisodeMobile";
import MentionCardProvider, { MentionCardProviderProps } from "./provider";

import styles from "./MentionCard.module.css";

/**
 *
 * The props that Episode accepts are not really documented prior to this comment, but
 * since I've tried putting everything into a episode context now I've taken the time to
 * put down what I've found. Use this when typing this file or when trying to figure out
 * if something is used or not.
 *
 * Types found in EpisodeContext
 */

/** Tell episode which component should render which source */
const getBodyComponent = (mentionCard: MentionCardType) => {
  const {
    channel: { channel_type },
    mentions,
  } = mentionCard;

  if (channel_type === SOURCE.NEWS) {
    return NewsMentionCardBody;
  }

  // If there's a single audio mention, render this as an audio hit.
  // The rest are meta or considered as meta.

  if (mentions.some((m) => m.context === MENTION_TYPE.TEXT)) {
    return AudioMentionCardBody;
  }

  return TextMentionCardBody;
};

const MentionCard = ({ className }: { className?: string }) => {
  const mentionCard = useMentionCard();

  // props should be a Episode here when typing. But some settings may come along as well right now.
  const Body = getBodyComponent(mentionCard);

  return (
    <MentionCardBase className={className}>
      <Body />
    </MentionCardBase>
  );
};

const ResponsiveEpisode = (props: {
  className?: string;
  isMobile?: boolean;
}) => {
  const episodesData = useEpisodes();

  const mobile = props.isMobile || episodesData.mobile;

  const Component = useMemo(
    () => (mobile ? MentionCardMobile : MentionCard),
    [mobile]
  );

  // stop all playing instances when unmounting
  useEffect(
    () => () => {
      document.dispatchEvent(events.stopAllPlayingAudio);
    },
    []
  );

  return <Component {...props} />;
};

export const SkeletonMentionCard = ({ className }: { className?: string }) => (
  <div className={cx(className, styles.placeholder)} />
);

const MentionCardWithLoader = ({
  className = "",
  isMobile,
  ...props
}: Omit<MentionCardProviderProps, "children"> & {
  hideActions?: boolean;
  hideTags?: boolean;
  hideReach?: boolean;
  hidePrValue?: boolean;
  hideSentiment?: boolean;
  className?: string;
  isMobile?: boolean;
}) => {
  return (
    <MentionCardProvider {...props}>
      <ResponsiveEpisode className={className} isMobile={isMobile} />
    </MentionCardProvider>
  );
};

export default MentionCardWithLoader;
